import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, ArrowDown, Check, X as XIcon, Star, Sparkles, Zap,
  Target, TrendingUp, PlayCircle, Rocket, BarChart3, Brain,
} from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { portfolioProjects, clientLogos } from "@/i18n/translations";
import { blogPosts } from "@/i18n/blogPosts";
import Marquee from "@/components/Marquee";
import ScrollReveal from "@/components/ScrollReveal";
import Testimonials from "@/components/Testimonials";
import AnchorStat from "@/components/AnchorStat";
import FaqSection from "@/components/FaqSection";

const HERO_VIDEO = "https://videos.pexels.com/video-files/3129957/3129957-hd_1920_1080_25fps.mp4";

const Home = ({ onOpenAudit }) => {
  const { t, lang } = useLang();
  const [showStickyCta, setShowStickyCta] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowStickyCta(window.scrollY > 1400);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <main data-testid="home-page" className="bg-brand-bg text-white relative">
      {/* HERO with VIDEO + STARFIELD + SCANLINES */}
      <section
        data-testid="hero-section"
        className="relative min-h-[90vh] flex flex-col justify-end pt-32 pb-12 overflow-hidden scanlines"
      >
        <video
          className="absolute inset-0 w-full h-full object-cover opacity-25 mix-blend-luminosity"
          autoPlay muted loop playsInline preload="metadata"
          poster="https://images.unsplash.com/photo-1558470598-a5dda9640f68?auto=format&fit=crop&w=1920&q=80"
        >
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-brand-bg/30 via-brand-bg/70 to-brand-bg" />
        <div className="aurora opacity-60 pointer-events-none"><div className="aurora-band" /></div>
        <div className="starfield" />

        <div
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage: "linear-gradient(to right, #ffffff10 1px, transparent 1px), linear-gradient(to bottom, #ffffff10 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />

        <div className="relative mx-auto max-w-[1400px] w-full px-4 sm:px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-6 sm:mb-10 ml-3">
            <span className="relative w-2.5 h-2.5 rounded-full bg-brand-purple pulse-dot" />
            <span className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.25em] text-zinc-200">
              {t.hero.tag}
            </span>
            <span className="hidden sm:inline-block flex-1 h-px bg-brand-border max-w-[180px]" />
            <span className="hidden sm:inline-block text-xs font-mono uppercase tracking-[0.25em] text-zinc-500">
              {t.hero.since}
            </span>
          </div>

          <ScrollReveal>
            <h1
              data-testid="hero-headline"
              className="font-heading font-black uppercase leading-[0.85] tracking-[-0.04em] text-[11vw] sm:text-[12vw] md:text-[10vw] lg:text-[8rem]"
            >
              <span className="block">{t.hero.headlineLine1}</span>
              <span className="block">
                <span className="text-white">{t.hero.headlineLine2.replace(".", "")}</span>
                <span className="text-brand-pink text-[1.1em]">.</span>
              </span>
            </h1>
          </ScrollReveal>

          <div className="mt-8 sm:mt-12 grid lg:grid-cols-12 gap-6 sm:gap-8 items-end">
            <ScrollReveal delay={150} className="lg:col-span-5">
              <p className="text-sm sm:text-base md:text-lg text-zinc-300 leading-relaxed max-w-md">
                {t.hero.sub}
              </p>
            </ScrollReveal>

            <ScrollReveal delay={250} className="lg:col-span-5 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                data-testid="hero-cta-audit"
                onClick={onOpenAudit}
                className="btn-holo inline-flex items-center justify-center gap-2 font-bold uppercase tracking-wide px-5 sm:px-7 py-3 sm:py-4 rounded-full text-xs sm:text-sm"
              >
                <Sparkles className="w-4 h-4" />
                {lang === "bg" ? "Безплатен Audit" : "Free Audit"}
                <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
              </button>
              <Link
                to="/portfolio"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center gap-2 btn-outline-glow text-white font-bold uppercase tracking-wide px-5 sm:px-7 py-3 sm:py-4 rounded-full text-xs sm:text-sm"
              >
                <PlayCircle className="w-4 h-4" />
                {lang === "bg" ? "Виж нашата работа" : "See our work"}
              </Link>
            </ScrollReveal>

            <ScrollReveal delay={350} className="hidden lg:block lg:col-span-2 lg:text-right">
              <div className="flex lg:justify-end items-center gap-2 text-zinc-500 text-xs font-mono uppercase tracking-[0.2em]">
                <ArrowDown className="w-4 h-4 text-brand-purpleLight animate-bounce" />
                {t.hero.scroll}
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ANCHOR STAT */}
      <AnchorStat />

      {/* METRICS — static grid */}
      <section data-testid="metrics-section" className="relative border-b border-brand-border bg-brand-bgSecond py-10">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="flex items-center justify-between gap-8 overflow-x-auto whitespace-nowrap scrollbar-hide">
            {t.metrics.items.map((m, i) => (
              <div
                key={i}
                className="flex flex-col gap-2 shrink-0"
                data-testid={`metric-${i}`}
              >
                <p className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-white">
                  {m.value}
                </p>
                <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">
                  {m.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOLO MARQUEE */}
      <Marquee items={t.marquee} variant="holo" testId="hero-services-marquee" />

      {/* SERVICES */}
      <section data-testid="services-section" className="relative py-28 md:py-36 border-b border-brand-border overflow-hidden">
        <div className="aurora opacity-30 pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-8 items-end mb-16">
            <div className="lg:col-span-7">
              <p className="text-xs font-mono uppercase tracking-[0.25em] text-brand-purpleLight mb-6">
                {t.services.tag}
              </p>
              <h2 className="font-heading text-4xl md:text-6xl font-black uppercase leading-[0.95] tracking-tighter">
                {lang === "bg" ? (
                  <>Дигитални услуги, проектирани да <span className="text-white">растат</span> с теб</>
                ) : (
                  <>Digital services engineered to <span className="text-white">scale</span> with you</>
                )}
              </h2>
            </div>
            <div className="lg:col-span-5 flex items-end lg:justify-end">
              <Link
                to="/services"
                data-testid="services-see-all"
                className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-[0.2em] text-zinc-300 hover:text-brand-purpleLight transition-colors link-slide whitespace-nowrap"
              >
                {t.services.seeAll}
                <ArrowUpRight className="w-4 h-4 shrink-0" />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {t.services.list.map((s, i) => (
              <ScrollReveal
                key={s.num} delay={i * 60} as="article"
                className="holo-card bento-cell rounded-2xl p-8 md:p-10 bg-brand-surface/60 border border-brand-border group hover:bg-brand-surface transition-colors"
              >
                <div className="flex items-start justify-between mb-8">
                  <span className="font-mono text-xs uppercase tracking-[0.25em] text-brand-purpleLight">
                    {s.num}
                  </span>
                  <ArrowUpRight className="w-5 h-5 text-zinc-500 group-hover:text-brand-pink group-hover:rotate-45 transition-transform" />
                </div>
                <h3 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight mb-4 leading-tight">
                  {s.title}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed mb-6">{s.desc}</p>
                <ul className="space-y-2">
                  {s.bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-3 text-sm text-zinc-200">
                      <span className="mt-[7px] w-2 h-2 rounded-full bg-brand-purpleLight shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
              </ScrollReveal>
            ))}

            {/* Bonus tile — now alien-styled, holo border + chrome heading */}
            <ScrollReveal
              delay={t.services.list.length * 60}
              className="holo-card relative rounded-2xl p-8 md:p-10 overflow-hidden flex flex-col justify-between bg-brand-bgSecond border border-brand-purple/40"
            >
              <div className="aurora opacity-25" />
              <div className="relative">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand-purpleLight inline-flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5" />
                  {lang === "bg" ? "+ AI Bonus" : "+ AI Bonus"}
                </p>
              </div>
              <div className="relative">
                <h3 className="font-heading text-3xl md:text-4xl font-black uppercase leading-[0.9] tracking-tighter mb-6">
                  <span className="text-white">
                    {lang === "bg" ? "Безплатен AI audit за 5 минути" : "Free AI audit in 5 minutes"}
                  </span>
                </h3>
                <button
                  data-testid="services-bonus-cta"
                  onClick={onOpenAudit}
                  className="btn-holo inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide px-5 py-3 rounded-full"
                >
                  {lang === "bg" ? "Стартирай audit" : "Run the audit"}
                  <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
                </button>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section data-testid="why-section" className="relative py-28 md:py-36 border-b border-brand-border bg-brand-bgSecond overflow-hidden">
        <div className="aurora opacity-25 pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="max-w-3xl mb-16">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-brand-purpleLight mb-6">
              {t.why.tag}
            </p>
            <h2 className="font-heading text-4xl md:text-6xl font-black uppercase leading-[0.95] tracking-tighter">
              {lang === "bg" ? (
                <>Какво ни <span className="text-white">отличава</span></>
              ) : (
                <>What sets us <span className="text-white">apart</span></>
              )}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {t.why.items.map((it, i) => {
              const Icon = [Rocket, BarChart3, Brain][i] || Rocket;
              return (
                <ScrollReveal
                  key={i} delay={i * 100}
                  className="holo-card rounded-2xl p-8 bg-brand-surface/70 border border-brand-border bento-cell"
                >
                  <Icon className="w-8 h-8 text-brand-purpleLight mb-6" strokeWidth={1.4} />
                  <div className="flex items-baseline gap-2 mb-4">
                    <span className="font-heading text-5xl md:text-6xl font-black tracking-tighter text-white">
                      {it.stat}
                    </span>
                    <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
                      {it.unit}
                    </span>
                  </div>
                  <h3 className="font-heading text-xl font-bold uppercase mb-3">{it.title}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{it.desc}</p>
                </ScrollReveal>
              );
            })}
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <ScrollReveal className="rounded-2xl p-8 md:p-10 bg-brand-surface/60 border border-brand-border">
              <h4 className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-zinc-500 mb-6">
                {t.why.compareLeft.title}
              </h4>
              <ul className="space-y-4">
                {t.why.compareLeft.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <span className="mt-1 w-5 h-5 grid place-items-center rounded-full border border-brand-borderStrong shrink-0">
                      <XIcon className="w-3 h-3 text-zinc-500" strokeWidth={3} />
                    </span>
                    <span className="text-zinc-400 line-through decoration-brand-borderStrong">
                      {b}
                    </span>
                  </li>
                ))}
              </ul>
            </ScrollReveal>
            <ScrollReveal
              delay={120}
              className="holo-card relative rounded-2xl p-8 md:p-10 bg-brand-surface/60 overflow-hidden"
            >
              <div className="aurora opacity-25" />
              <h4 className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-white mb-6 relative">
                {t.why.compareRight.title}
              </h4>
              <ul className="space-y-4 relative">
                {t.why.compareRight.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <span className="mt-1 w-5 h-5 grid place-items-center rounded-full bg-brand-purple shrink-0">
                      <Check className="w-3 h-3 text-white" strokeWidth={3} />
                    </span>
                    <span className="text-white font-medium">{b}</span>
                  </li>
                ))}
              </ul>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section data-testid="process-section" className="py-28 md:py-36 border-b border-brand-border">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-8 items-end mb-16">
            <div className="lg:col-span-8">
              <p className="text-xs font-mono uppercase tracking-[0.25em] text-brand-purpleLight mb-6">
                {t.process.tag}
              </p>
              <h2 className="font-heading text-4xl md:text-6xl font-black uppercase leading-[0.95] tracking-tighter">
                {lang === "bg" ? (
                  <>Стъпките към <span className="text-white">успеха</span></>
                ) : (
                  <>Steps to <span className="text-white">success</span></>
                )}
              </h2>
            </div>
            <p className="lg:col-span-4 text-zinc-400 text-sm md:text-base leading-relaxed">
              {t.process.sub}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {t.process.steps.map((s, i) => {
              const Icon = [Target, Sparkles, Zap, TrendingUp][i] || Star;
              return (
                <ScrollReveal
                  key={s.num} delay={i * 100}
                  className="holo-card rounded-2xl bg-brand-surface/60 border border-brand-border p-5 md:p-6 group hover:bg-brand-surface transition-colors relative bento-cell"
                >
                  <div className="flex items-start justify-between mb-6">
                    <span className="font-mono text-xs uppercase tracking-[0.25em] text-brand-purpleLight">
                      {lang === "bg" ? "Стъпка" : "Step"} {s.num}
                    </span>
                    <div className="w-10 h-10 rounded-full bg-brand-purple/10 grid place-items-center group-hover:bg-brand-purple/30 transition-colors">
                      <Icon className="w-4 h-4 text-brand-purpleLight" strokeWidth={1.5} />
                    </div>
                  </div>
                  <h3 className="font-heading text-xl md:text-2xl font-black uppercase tracking-tighter mb-3 break-words">
                    {s.title}
                  </h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{s.desc}</p>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      <Marquee items={clientLogos} variant="glass" testId="clients-marquee" />

      <Testimonials />

      {/* PORTFOLIO */}
      <section data-testid="portfolio-section" className="py-28 md:py-36 border-b border-brand-border">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-8 items-end mb-16">
            <div className="lg:col-span-8">
              <p className="text-xs font-mono uppercase tracking-[0.25em] text-brand-purpleLight mb-6">
                {t.portfolio.tag}
              </p>
              <h2 className="font-heading text-4xl md:text-6xl font-black uppercase leading-[0.95] tracking-tighter">
                {lang === "bg" ? (
                  <>Бизнеси, които <span className="text-white">растат</span> с нас</>
                ) : (
                  <>Brands <span className="text-white">growing</span> with us</>
                )}
              </h2>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Link
                to="/portfolio" data-testid="portfolio-see-all"
                className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-[0.2em] text-zinc-300 hover:text-brand-purpleLight transition-colors link-slide"
              >
                {t.portfolio.seeAll}
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {portfolioProjects.slice(0, 3).map((p, i) => (
              <ScrollReveal
                key={p.slug} delay={i * 100}
                className="group relative overflow-hidden rounded-2xl bg-brand-surface border border-brand-border holo-card"
              >
                <Link to="/portfolio" data-testid={`portfolio-card-${p.slug}`} className="block">
                  <div className="relative overflow-hidden aspect-[4/3]">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-bg via-brand-bg/40 to-transparent" />
                    <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                      {p.tags.map((tag) => (
                        <span key={tag} className="px-2.5 py-1 bg-brand-bg/80 backdrop-blur text-[10px] font-mono uppercase tracking-[0.2em] text-brand-purpleLight border border-brand-purple/40 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="p-6 md:p-8 flex items-end justify-between gap-4">
                    <div>
                      <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-2">{p.category}</p>
                      <h3 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight">{p.name}</h3>
                      <p className="mt-2 text-sm text-white font-mono font-semibold">{p.result[lang]}</p>
                    </div>
                    <div className="w-12 h-12 rounded-full border border-brand-purple/50 grid place-items-center group-hover:border-brand-pink group-hover:bg-brand-pink/10 transition-colors shrink-0">
                      <ArrowUpRight className="w-5 h-5 text-white" strokeWidth={2.5} />
                    </div>
                  </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section data-testid="pricing-section" className="relative py-28 md:py-36 border-b border-brand-border bg-brand-bgSecond overflow-hidden">
        <div className="aurora opacity-25 pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-brand-purpleLight mb-6">
              {t.pricing.tag}
            </p>
            <h2 className="font-heading text-4xl md:text-6xl font-black uppercase leading-[0.95] tracking-tighter mb-6">
              {lang === "bg" ? (
                <>Изберете <span className="text-white">пакета</span></>
              ) : (
                <>Pick the <span className="text-white">right fit</span></>
              )}
            </h2>
            <p className="text-zinc-400 text-base md:text-lg">{t.pricing.sub}</p>

            {/* Urgency badge */}
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-pink/40 bg-brand-pink/5 text-brand-pinkLight text-xs font-mono uppercase tracking-[0.2em]">
              <span className="w-2 h-2 rounded-full bg-brand-pink animate-pulse" />
              {lang === "bg" ? "3 свободни слота за май 2026" : "3 slots open for May 2026"}
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {t.pricing.plans.map((p, i) => {
              const popular = i === 1;
              return (
                <ScrollReveal
                  key={p.name} delay={i * 100}
                  className={`relative rounded-2xl p-8 md:p-10 bento-cell ${popular ? "holo-card bg-brand-surface lg:scale-[1.04] shadow-[0_0_60px_-10px_rgba(168,85,247,0.45)]" : "border border-brand-border bg-brand-surface/60 hover:bg-brand-surface card-glow"} transition-colors`}
                >
                  {popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 btn-holo text-white text-[10px] font-mono uppercase tracking-[0.25em] px-4 py-1.5 rounded-full">
                      {t.pricing.mostPopular}
                    </span>
                  )}
                  <h3 className="font-heading text-3xl font-black uppercase tracking-tight mb-2">{p.name}</h3>
                  <p className="text-sm text-brand-purpleLight font-mono mb-4">{p.tagline}</p>
                  <p className="text-sm text-zinc-400 leading-relaxed mb-8 min-h-[60px]">{p.desc}</p>
                  <ul className="space-y-3 mb-10 min-h-[200px]">
                    {p.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-3 text-sm text-zinc-200">
                        <span className="mt-1 w-4 h-4 rounded-full bg-brand-purple grid place-items-center shrink-0">
                          <Check className="w-2.5 h-2.5 text-white" strokeWidth={3.5} />
                        </span>
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    data-testid={`pricing-cta-${p.name.toLowerCase()}`}
                    className={`inline-flex w-full items-center justify-center gap-2 px-7 py-4 text-sm font-bold uppercase tracking-wide rounded-full transition-colors ${popular ? "btn-holo text-white" : "btn-outline-glow text-white"}`}
                  >
                    {t.pricing.cta}
                    <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
                  </Link>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FaqSection />

      {/* BLOG */}
      <section data-testid="blog-section" className="py-28 md:py-36 border-b border-brand-border">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-8 items-end mb-16">
            <div className="lg:col-span-8">
              <p className="text-xs font-mono uppercase tracking-[0.25em] text-brand-purpleLight mb-6">
                {t.blog.tag}
              </p>
              <h2 className="font-heading text-4xl md:text-6xl font-black uppercase leading-[0.95] tracking-tighter">
                {lang === "bg" ? (
                  <>Последни <span className="text-white">статии</span></>
                ) : (
                  <>Latest <span className="text-white">insights</span></>
                )}
              </h2>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Link
                to="/blog" data-testid="blog-see-all"
                className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-[0.2em] text-zinc-300 hover:text-brand-purpleLight transition-colors link-slide"
              >
                {t.blog.seeAll}
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {blogPosts.slice(0, 3).map((p, i) => (
              <ScrollReveal
                key={p.slug} delay={i * 100} as="article"
                className="rounded-2xl border border-brand-border bg-brand-surface/60 group hover:bg-brand-surface transition-colors bento-cell card-glow overflow-hidden"
              >
                <Link to={`/blog/${p.slug}`} data-testid={`home-blog-card-${p.slug}`} className="block">
                  <div className="aspect-[16/10] overflow-hidden bg-brand-bg relative">
                    <img src={p.image} alt={p.title[lang]} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-bg/70 to-transparent" />
                  </div>
                  <div className="p-6 md:p-8">
                    <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-4">
                      <span className="text-brand-purpleLight">{p.cat[lang]}</span>
                      <span>•</span>
                      <span>{p.time[lang]}</span>
                    </div>
                    <h3 className="font-heading text-xl md:text-2xl font-bold leading-snug mb-3 group-hover:text-white transition-colors">
                      {p.title[lang]}
                    </h3>
                    <p className="text-sm text-zinc-400 leading-relaxed mb-6 line-clamp-3">{p.excerpt[lang]}</p>
                    <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-zinc-300 group-hover:text-brand-purpleLight transition-colors">
                      {t.blog.readMore}
                      <ArrowUpRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* MEGA CTA */}
      <section data-testid="mega-cta-section" className="relative py-32 md:py-44 overflow-hidden border-b border-brand-border">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-bg via-brand-bgSecond to-brand-bg pointer-events-none" />
        <div className="starfield opacity-20" />

        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-start">
          {/* LEFT col */}
          <div className="lg:col-span-5">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-brand-purpleLight mb-6 inline-flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> {t.finalCta.tag}
            </p>
            <h2 className="font-heading font-black uppercase leading-[0.85] tracking-tighter text-5xl md:text-6xl lg:text-7xl text-white">
              {lang === "bg" ? (
                <>Време е<span className="text-brand-pink">.</span></>
              ) : (
                <>It's time<span className="text-brand-pink">.</span></>
              )}
            </h2>
          </div>

          {/* RIGHT col */}
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="text-base md:text-lg text-zinc-300 leading-relaxed mb-8">
              {t.finalCta.sub}
            </p>
            <div className="flex flex-wrap gap-4 mb-8">
              <button
                data-testid="mega-cta-audit" onClick={onOpenAudit}
                className="btn-holo inline-flex items-center justify-center gap-2 text-white font-bold uppercase tracking-wide px-7 py-4 rounded-full text-sm"
              >
                <Sparkles className="w-4 h-4" />
                {lang === "bg" ? "Безплатен Audit" : "Free Audit"}
              </button>
              <Link
                to="/contact" data-testid="mega-cta-contact"
                className="inline-flex items-center justify-center gap-2 btn-outline-glow text-white font-bold uppercase tracking-wide px-7 py-4 rounded-full text-sm"
              >
                {t.finalCta.ctaContact}
                <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* OUTLINE BIG MARQUEE */}
      <Marquee
        items={["RESULTS NOT PROMISES", "GROWTH ENGINEERED", "DATA × DESIGN", "PRIME COLLECTIVE"]}
        variant="outline" speed="marquee-slow" testId="outline-marquee"
      />

      {/* Sticky Audit CTA */}
      {/* <button
        data-testid="sticky-audit-cta"
        onClick={onOpenAudit}
        aria-label={lang === "bg" ? "Безплатен Audit" : "Free Audit"}
        className={`fixed bottom-6 left-6 z-40 inline-flex items-center gap-2 btn-holo text-white font-bold uppercase tracking-wide px-5 py-4 rounded-full text-xs transition-all duration-500 ${showStickyCta ? "translate-y-0 opacity-100" : "translate-y-24 opacity-0 pointer-events-none"}`}
      >
        <Sparkles className="w-4 h-4" />
        {lang === "bg" ? "Безплатен Audit" : "Free Audit"}
      </button> */}
    </main>
  );
};

export default Home;
